export type ValidationResult = {
  verification: boolean
  message: string
}

const validateNaturalPerson = (digits: number[], identification: string, d10: number): ValidationResult => {
  const coefficients = [2, 1, 2, 1, 2, 1, 2, 1, 2]
  const sum = coefficients.reduce((acc, coeff, idx) => {
    const prod = digits[idx] * coeff
    return acc + (prod >= 10 ? prod - 9 : prod)
  }, 0)

  const remainder = sum % 10
  const checkDigit = remainder === 0 ? 0 : 10 - remainder

  if (checkDigit !== d10) {
    return {
      verification: false,
      message: 'El número de cédula de la persona natural es incorrecto.',
    }
  }

  if (identification.length > 10 && identification.slice(10, 13) !== '001') {
    return {
      verification: false,
      message: 'El ruc de la persona natural debe terminar con 001',
    }
  }

  return { verification: true, message: '' }
}

const validatePublicSociety = (digits: number[], identification: string, d9: number): ValidationResult => {
  const coefficients = [3, 2, 7, 6, 5, 4, 3, 2]
  const sum = coefficients.reduce((acc, coeff, idx) => acc + digits[idx] * coeff, 0)

  const remainder = sum % 11
  const checkDigit = remainder === 0 ? 0 : 11 - remainder

  if (checkDigit !== d9) {
    return {
      verification: false,
      message: 'El ruc de la empresa del sector público es incorrecto.',
    }
  }

  if (identification.slice(9, 13) !== '0001') {
    return {
      verification: false,
      message: 'El ruc de la empresa del sector público debe terminar con 0001',
    }
  }

  return { verification: true, message: '' }
}

const validatePrivateSociety = (digits: number[], identification: string, d10: number): ValidationResult => {
  const coefficients = [4, 3, 2, 7, 6, 5, 4, 3, 2]
  const sum = coefficients.reduce((acc, coeff, idx) => acc + digits[idx] * coeff, 0)

  const remainder = sum % 11
  const checkDigit = remainder === 0 ? 0 : 11 - remainder

  if (checkDigit !== d10) {
    return {
      verification: false,
      message: 'El ruc de la empresa del sector privado es incorrecto.',
    }
  }

  if (identification.slice(10, 13) !== '001') {
    return {
      verification: false,
      message: 'El ruc de la empresa del sector privado debe terminar con 001',
    }
  }

  return { verification: true, message: '' }
}

export const validateDocument = (identification: string): ValidationResult => {
  if (/\D/.test(identification)) {
    return {
      verification: false,
      message: 'No puede ingresar caracteres en el número',
    }
  }

  if (identification.length < 10) {
    return {
      verification: false,
      message: 'El número de cédula ingresado no es válido',
    }
  }

  const province = parseInt(identification.slice(0, 2), 10)
  if (province < 1 || province > 24) {
    return {
      verification: false,
      message: 'El código de la provincia (dos primeros dígitos) es inválido',
    }
  }

  const digits = identification.split('').map((char) => parseInt(char, 10))
  const [, , d3, , , , , , d9, d10] = digits

  if (d3 === 7 || d3 === 8) {
    return {
      verification: false,
      message: 'El tercer dígito ingresado es inválido',
    }
  }

  if (d3 < 6) {
    return validateNaturalPerson(digits, identification, d10)
  }

  if (d3 === 6) {
    return validatePublicSociety(digits, identification, d9)
  }

  if (d3 === 9) {
    return validatePrivateSociety(digits, identification, d10)
  }

  return {
    verification: true,
    message: '',
  }
}
